import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import MathJax from 'react-mathjax2';

import { selectors as localeSelectors } from '../../../store/locale';

import {
  actions as competitionsActions,
  selectors as competitionsSelectors,
} from '../../../store/competitions';

import {
  selectors as seriesesSelectors,
  actions as seriesesActions,
} from '../../../store/serieses';

import {
  actions as questionsActions,
  selectors as questionsSelectors,
} from '../../../store/questions';

import { selectors as choicesSelectors } from '../../../store/choices';

import {
  actions as tagsActions,
  selectors as tagsSelectors,
} from '../../../store/tags';

import {
  actions as contentsActions,
  selectors as contentsSelectors,
} from '../../../store/contents';

import Question from '../../../models/Question';

import {
  COMPETITION_TYPES,
  CHOICE_LAYOUTS,
  CONTENT_TYPES,
} from '../../common/enums';

import {
  CompetitionPropType,
  SeriesPropType,
  QuestionPropType,
  ChoicePropType,
  TagPropType,
  PaginationPropType,
} from '../../common/types';

import Spinner from '../../common/Spinner';
import Markdown from '../../common/Markdown';

import Practice from './Practice';
import Search, { PER_PAGE } from './Search';
import ArchiveStats from './ArchiveStats';

const PAGE_PRACTICE = 'practice';
const PAGE_SEARCH = 'search';
const PAGE_STATS = 'stats';
const PAGE_PDFS = 'pdfs';

const PAGES = [PAGE_PRACTICE, PAGE_SEARCH, PAGE_STATS, PAGE_PDFS];

class Archive extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    fetchCompetitions: PropTypes.func.isRequired,
    fetchQuestions: PropTypes.func.isRequired,
    fetchTags: PropTypes.func.isRequired,
    fetchStats: PropTypes.func.isRequired,
    fetchContents: PropTypes.func.isRequired,
    questionLocale: PropTypes.string.isRequired,
    competitions: PropTypes.arrayOf(CompetitionPropType),
    competitionsLoading: PropTypes.bool.isRequired,
    serieses: PropTypes.arrayOf(SeriesPropType),
    questions: PropTypes.arrayOf(QuestionPropType),
    questionsPagination: PaginationPropType,
    questionsLoading: PropTypes.bool.isRequired,
    choices: PropTypes.arrayOf(ChoicePropType),
    tags: PropTypes.arrayOf(TagPropType),
    stats: PropTypes.object.isRequired,
    statsLoading: PropTypes.bool.isRequired,
    content: PropTypes.object,
    contentsLoading: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    competitions: [],
    serieses: [],
    questions: [],
    questionsPagination: null,
    choices: [],
    tags: [],
    content: null,
  };

  state = {
    page: PAGE_PRACTICE,
    printQuestions: [],
    printSolutions: false,
  };

  constructor(props) {
    super(props);

    const { slug } = props.match.params;
    if (slug && PAGES.includes(slug)) this.state.page = slug;
  }

  componentDidMount() {
    this.props.fetchCompetitions({
      type: COMPETITION_TYPES.KENGURU,
      is_public: true,
      is_archived: true,
      include: 'serieses',
    });

    this.props.fetchTags();
    this.props.fetchQuestions({ include: 'choices', limit: PER_PAGE });
    this.props.fetchContents();
  }

  onPageChange = page => {
    this.setState({ page });
  };

  onToggleShouldPrint = questionId => {
    const { questions, choices } = this.props;
    const { printQuestions } = this.state;

    let newPrintQuestions = null;
    if (printQuestions.some(q => q.id === questionId)) {
      newPrintQuestions = printQuestions.filter(q => q.id !== questionId);
    } else {
      const question = questions.find(q => q.id === questionId);
      const questionChoices = question.choice_ids.map(choiceId =>
        choices.find(c => c.id === choiceId)
      );

      const printQuestion = new Question({
        ...question,
        choices: questionChoices,
      });

      newPrintQuestions = [...printQuestions, printQuestion];
    }

    this.setState({ printQuestions: newPrintQuestions });
  };

  onClearPrintQuestions = () => {
    this.setState({ printQuestions: [] });
  };

  onPrint = () => {
    this.setState({ printSolutions: false }, () => {
      window.print();
    });
  };

  onPrintSolutions = () => {
    this.setState({ printSolutions: true }, () => {
      window.print();
    });
  };

  renderPageButtonInner = (page, text) => (
    <div className="row d-flex align-items-center">
      <div className={`HomeArchive-selector-${page}-logo col col-sm-auto`} />
      <div className="HomeArchive-selector-text col col-sm-auto mt-sm-4 mx-auto">
        {text}
      </div>
    </div>
  );

  renderPageButton = (page, text) =>
    this.state.page === page ? (
      <button
        type="button"
        className="HomeArchive-selector text-center active"
        disabled
      >
        {this.renderPageButtonInner(page, text)}
      </button>
    ) : (
      <Link
        to={`/home/archive/${page}`}
        className="HomeArchive-selector text-center"
        onClick={() => this.onPageChange(page)}
      >
        {this.renderPageButtonInner(page, text)}
      </Link>
    );

  renderPrintChoices = (question, choices) => {
    const { questionLocale } = this.props;

    const isFullWidthChoice = index => {
      if (choices.length !== 5) return false;

      switch (question.choice_layout) {
        case CHOICE_LAYOUTS.STACKED:
          return true;

        case CHOICE_LAYOUTS.L_4_1:
          return index === 4;

        case CHOICE_LAYOUTS.L_3_1_1:
          return index > 2;

        case CHOICE_LAYOUTS.L_2_1_1_1:
          return index > 1;

        default:
          return false;
      }
    };

    const shouldBreak = index => {
      if (choices.length !== 5) return false;

      switch (question.choice_layout) {
        case CHOICE_LAYOUTS.L_3_2:
          return index === 2;

        case CHOICE_LAYOUTS.L_2_2_1:
          return index === 1 || index === 3;

        default:
          return false;
      }
    };

    return choices.map((choice, choiceIndex) => (
      <React.Fragment key={choice.id}>
        <div
          className={classNames('col-auto', 'mb-1', {
            'w-100': isFullWidthChoice(choiceIndex),
          })}
        >
          <div className="row">
            <div className="col-auto pr-1">
              <span>({String.fromCharCode(65 + choiceIndex)})</span>
            </div>

            <div className="col-auto">
              <MathJax.Text
                text={choice.descriptionWithFallback(questionLocale)}
              />
            </div>
          </div>
        </div>

        {shouldBreak(choiceIndex) && <div className="w-100" />}
      </React.Fragment>
    ));
  };

  renderPrintQuestion = question => {
    const { questionLocale } = this.props;
    const { printSolutions } = this.state;

    const correctChoiceIndex = question.choice_ids.findIndex(
      cid => cid === question.correct_choice
    );

    return (
      <div key={question.id} className="HomeArchive-print-question mt-5">
        <div className="font-weight-bold">{question.number}.</div>

        <MathJax.Text text={question.descriptionWithFallback(questionLocale)} />

        <div className="HomeArchive-print-choices row mt-4">
          {this.renderPrintChoices(
            question,
            question.choice_ids.map(choiceId =>
              question.choices.find(c => c.id === choiceId)
            )
          )}
        </div>

        {printSolutions && (
          <div className="mt-3">
            <div>
              <FormattedHTMLMessage
                id="home-archive-print-answer"
                defaultMessage="<b>Vastaus:</b> {answer}"
                values={{
                  answer: String.fromCharCode(65 + correctChoiceIndex),
                }}
              />
            </div>

            <div className="font-weight-bold">
              <FormattedMessage
                id="home-archive-print-example"
                defaultMessage="Esimerkkiratkaisu:"
              />
            </div>

            <MathJax.Text text={question.exampleWithFallback(questionLocale)} />
          </div>
        )}
      </div>
    );
  };

  render() {
    const {
      fetchQuestions,
      fetchStats,
      questionLocale,
      competitions,
      competitionsLoading,
      serieses,
      questions,
      questionsPagination,
      questionsLoading,
      choices,
      tags,
      stats,
      statsLoading,
      content,
      contentsLoading,
    } = this.props;

    const { locale } = this.props.intl;

    const { page, printQuestions } = this.state;

    const sortedPrintQuestions = [...printQuestions].sort((a, b) => {
      const as = serieses.find(s => s.id === a.series_id);
      const ac = competitions.find(c => c.id === as.competition_id);

      const bs = serieses.find(s => s.id === b.series_id);
      const bc = competitions.find(c => c.id === bs.competition_id);

      if (ac.created_at !== bc.created_at) {
        return ac.created_at < bc.created_at ? 1 : -1;
      }

      if (as.created_at !== bs.created_at) {
        return as.created_at < bs.created_at ? 1 : -1;
      }

      const an = as.question_ids.findIndex(qid => qid === a.id);
      const bn = bs.question_ids.findIndex(qid => qid === b.id);

      return an < bn ? -1 : 1;
    });

    return (
      <div className="HomeArchive">
        <div className="HomeArchive-print">
          {sortedPrintQuestions.map(this.renderPrintQuestion)}
        </div>

        <div className="container d-print-none">
          <div className="text-center">
            <div className="HomeArchive-header d-inline-flex align-items-center justify-content-center">
              <div className="HomeArchive-header-icon mr-3" />

              <h2 className="my-0">
                <FormattedMessage
                  id="home-archive-header"
                  defaultMessage="Arkisto"
                />
              </h2>
            </div>
          </div>

          <div className="row mb-4 d-flex justify-content-center">
            <div className="col-auto mt-3">
              {this.renderPageButton(
                PAGE_PRACTICE,
                <FormattedMessage
                  id="home-archive-practice"
                  defaultMessage="Harjoittele vanhoja Kengurukisoja"
                />
              )}
            </div>

            <div className="col-auto mt-3">
              {this.renderPageButton(
                PAGE_SEARCH,
                <FormattedMessage
                  id="home-archive-search"
                  defaultMessage="Hae vanhoja tehtäviä"
                />
              )}
            </div>

            <div className="col-auto mt-3">
              {this.renderPageButton(
                PAGE_STATS,
                <FormattedMessage
                  id="home-archive-stats"
                  defaultMessage="Tutki vanhoja tilastoja"
                />
              )}
            </div>

            <div className="col-auto mt-3">
              {this.renderPageButton(
                PAGE_PDFS,
                <FormattedMessage
                  id="home-archive-pdfs"
                  defaultMessage="Vanhat kisat PDF-muodossa"
                />
              )}
            </div>
          </div>

          <hr />

          <div
            className={classNames({
              'd-none': page !== PAGE_SEARCH,
              'd-block': page === PAGE_SEARCH,
            })}
          >
            <Search
              fetchQuestions={fetchQuestions}
              fetchStats={fetchStats}
              questionLocale={questionLocale}
              competitions={competitions}
              serieses={serieses}
              questions={questions}
              questionsPagination={questionsPagination}
              questionsLoading={questionsLoading}
              choices={choices}
              tags={tags}
              stats={stats}
              statsLoading={statsLoading}
              printQuestions={printQuestions}
              onToggleShouldPrint={this.onToggleShouldPrint}
              onClearPrintQuestions={this.onClearPrintQuestions}
              onPrint={this.onPrint}
              onPrintSolutions={this.onPrintSolutions}
            />
          </div>

          <div
            className={classNames({
              'd-none': page !== PAGE_PRACTICE,
              'd-block': page === PAGE_PRACTICE,
            })}
          >
            <Practice
              competitions={competitions}
              competitionsLoading={competitionsLoading}
              serieses={serieses}
            />
          </div>

          <div
            className={classNames({
              'd-none': page !== PAGE_STATS,
              'd-block': page === PAGE_STATS,
            })}
          >
            <ArchiveStats
              competitions={competitions}
              competitionsLoading={competitionsLoading}
              serieses={serieses}
              fetchStats={fetchStats}
              stats={stats}
            />
          </div>

          <div
            className={classNames({
              'd-none': page !== PAGE_PDFS,
              'd-block': page === PAGE_PDFS,
            })}
          >
            {contentsLoading || !content ? (
              <Spinner center />
            ) : (
              <div className="markdown">
                <Markdown source={content.body[locale]} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  state => ({
    questionLocale: localeSelectors.questionLocale(state),
    competitions: competitionsSelectors.competitions(state),
    competitionsLoading: competitionsSelectors.loading(state),
    serieses: seriesesSelectors.serieses(state),
    questions: questionsSelectors.questions(state),
    questionsPagination: questionsSelectors.pagination(state),
    questionsLoading: questionsSelectors.loading(state),
    choices: choicesSelectors.choices(state),
    tags: tagsSelectors.tags(state),
    stats: seriesesSelectors.stats(state),
    statsLoading: seriesesSelectors.statsLoading(state),
    contents: contentsSelectors.contents(state),
    contentsLoading: contentsSelectors.loading(state),
  }),
  dispatch =>
    bindActionCreators(
      {
        fetchCompetitions: competitionsActions.fetchAll,
        fetchQuestions: questionsActions.fetchAll,
        fetchTags: tagsActions.fetchAll,
        fetchStats: seriesesActions.fetchStats,
        fetchContents: contentsActions.fetchAll,
      },
      dispatch
    ),
  (state, dispatch, props) => ({
    content: state.contents.find(c => c.type === CONTENT_TYPES.ARCHIVE_PDFS),
    ...state,
    competitions: state.competitions.filter(
      c => c.type === COMPETITION_TYPES.KENGURU && c.is_public && c.is_archived
    ),
    ...dispatch,
    ...props,
  })
)(injectIntl(Archive));
